import { AlertTriangle, Copy, Edit3, Loader2, MapPin, Scale, X } from "lucide-react";
import { toast } from "sonner";
import { useState } from "react";
import { api, adminHeaders } from "@/lib/api";

const RISK_STYLES = {
        high: "border-[var(--deception)] text-[var(--deception)]",
        review: "border-[var(--ai)] text-[var(--ai)]",
        medium: "border-[var(--controversy)] text-[var(--controversy)]",
        low: "border-[var(--line-strong)] text-[var(--ink-soft)]",
};

const RISK_LABELS = {
        high: "Alto",
        review: "Requer revisão",
        medium: "Médio",
        low: "Baixo",
};

const riskBadgeText = (level) =>
        level === "review" ? RISK_LABELS.review : `Risco ${RISK_LABELS[level] || "Médio"}`;

export const LegalRiskPanel = ({
        editorialStatus = "published",
        findings = [],
        limit = 30,
        model,
        onEdit,
        provider,
        setEditorialStatus,
        setFindings,
        setLimit,
        topics,
}) => {
        const [scanning, setScanning] = useState(false);

        const scan = async () => {
                setScanning(true);
                try {
                        const payload = {
                                limit: Number(limit),
                                editorial_status: editorialStatus,
                                provider,
                        };
                        if (model?.trim()) payload.model = model.trim();

                        const response = await api.post(
                                "/admin/legal-risk/scan",
                                payload,
                                { headers: adminHeaders() }
                        );
                        setFindings(response.data || []);
                        if ((response.data || []).length === 0) {
                                toast.success("Sem riscos editoriais assinalados neste lote.");
                        } else {
                                toast.success(`${response.data.length} ponto(s) para revisão.`);
                        }
                } catch (error) {
                        toast.error(error.response?.data?.detail || "A análise de risco falhou.");
                } finally {
                        setScanning(false);
                }
        };

        const openFinding = (finding) => {
                const topic = topics.find(
                        (item) => item.id === finding.topic_id || item.slug === finding.slug
                );
                if (!topic) {
                        toast.error("Não encontrei este dossiê na lista carregada.");
                        return;
                }
                onEdit(topic);
        };

        const copySuggestion = async (suggestion) => {
                try {
                        await navigator.clipboard.writeText(suggestion);
                        toast.success("Frase copiada.");
                } catch {
                        toast.error("Não consegui copiar automaticamente.");
                }
        };

        const dismissFinding = (finding) => {
                setFindings(
                        findings.filter(
                                (item) =>
                                        !(
                                                item.topic_id === finding.topic_id &&
                                                item.risk_level === finding.risk_level &&
                                                item.rationale === finding.rationale
                                        )
                        )
                );
                toast.info("Achado removido da lista.");
        };

        return (
                <div className="adm-card p-6 relative">
                        <div className="absolute -top-3 left-6 stamp-ai stamp">Triagem jurídica</div>
                        <div className="mt-2 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5">
                                <div>
                                        <div className="flex items-center gap-2 text-[var(--ink)] mb-3">
                                                <Scale size={18} strokeWidth={1.5} />
                                                <div className="overline">Risco editorial nas publicações</div>
                                        </div>
                                        <p className="text-sm text-[var(--ink-soft)] max-w-2xl">
                                                A IA levanta candidatos e confirma cada um no dossiê completo antes de os mostrar. É triagem editorial, não parecer jurídico; Alto fica reservado para imputação grave, concreta e sem suporte visível.
                                        </p>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-[1fr_120px_auto] gap-3 w-full lg:w-auto">
                                        <label className="text-xs uppercase tracking-[0.18em] text-[var(--ink-soft)]">
                                                Estado
                                                <select
                                                        value={editorialStatus}
                                                        onChange={(event) => setEditorialStatus(event.target.value)}
                                                        className="mt-2 w-full border border-[var(--line-strong)] bg-[var(--bg-deep)] px-3 py-2 text-sm text-[var(--ink)]"
                                                >
                                                        <option value="published">Publicados</option>
                                                        <option value="review">Em revisão</option>
                                                        <option value="draft">Rascunhos</option>
                                                        <option value="archived">Arquivados</option>
                                                        <option value="all">Todos</option>
                                                </select>
                                        </label>
                                        <label className="text-xs uppercase tracking-[0.18em] text-[var(--ink-soft)]">
                                                Lote
                                                <input
                                                        type="number"
                                                        min="1"
                                                        max="100"
                                                        value={limit}
                                                        onChange={(event) => setLimit(event.target.value)}
                                                        className="mt-2 w-full border border-[var(--line-strong)] bg-transparent px-3 py-2 text-sm text-[var(--ink)]"
                                                />
                                        </label>
                                        <button
                                                type="button"
                                                onClick={scan}
                                                disabled={scanning}
                                                className="adm-btn adm-btn-primary self-end"
                                        >
                                                {scanning ? (
                                                        <>
                                                                <Loader2 size={14} strokeWidth={1.5} className="animate-spin" />
                                                                A confirmar...
                                                        </>
                                                ) : (
                                                        <>
                                                                <AlertTriangle size={14} strokeWidth={1.5} />
                                                                Analisar
                                                        </>
                                                )}
                                        </button>
                                </div>
                        </div>

                        <div className="mt-8 space-y-4">
                                {findings.map((finding) => (
                                        <article
                                                key={`${finding.topic_id}-${finding.risk_level}-${finding.rationale}`}
                                                className="border border-[var(--line)] bg-[var(--bg-deep)] p-5"
                                        >
                                                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                                                        <div className="min-w-0">
                                                                <div className="flex flex-wrap items-center gap-2 mb-2">
                                                                        <span className={`overline px-2 py-1 border ${RISK_STYLES[finding.risk_level] || RISK_STYLES.medium}`}>
                                                                                {riskBadgeText(finding.risk_level)}
                                                                        </span>
                                                                        {(finding.risk_types || []).map((type) => (
                                                                                <span key={type} className="text-[10px] uppercase tracking-[0.16em] text-[var(--ink-soft)] border border-[var(--line)] px-2 py-1">
                                                                                        {type}
                                                                                </span>
                                                                        ))}
                                                                </div>
                                                                <h2 className="font-serif text-2xl leading-tight text-[var(--ink)]">
                                                                        {finding.title}
                                                                </h2>
                                                                <p className="font-mono text-xs text-[var(--ink-faint)] mt-1">
                                                                        /topico/{finding.slug}
                                                                </p>
                                                        </div>
                                                        <button
                                                                type="button"
                                                                onClick={() => openFinding(finding)}
                                                                className="overline px-4 py-2 border border-[var(--line-strong)] text-[var(--ink)] hover:bg-[var(--ink)] hover:text-[var(--paper)] inline-flex items-center justify-center gap-2 shrink-0"
                                                        >
                                                                <Edit3 size={14} strokeWidth={1.5} />
                                                                Abrir
                                                        </button>
                                                        <button
                                                                type="button"
                                                                onClick={() => dismissFinding(finding)}
                                                                className="overline px-4 py-2 border border-[var(--line-strong)] text-[var(--ink-soft)] hover:text-[var(--deception)] hover:border-[var(--deception)] inline-flex items-center justify-center gap-2 shrink-0"
                                                        >
                                                                <X size={14} strokeWidth={1.5} />
                                                                Ignorar
                                                        </button>
                                                </div>

                                                {(finding.excerpts || []).length > 0 && (
                                                <div className="mt-4 space-y-2">
                                                                {(finding.locations || []).length > 0 && (
                                                                        <div className="flex flex-wrap items-center gap-2 text-xs text-[var(--ink-soft)]">
                                                                                <MapPin size={13} strokeWidth={1.5} />
                                                                                {finding.locations.map((location) => (
                                                                                        <span
                                                                                                key={location}
                                                                                                className="border border-[var(--line)] px-2 py-1 font-mono"
                                                                                        >
                                                                                                {location}
                                                                                        </span>
                                                                                ))}
                                                                        </div>
                                                                )}
                                                                {finding.excerpts.map((excerpt) => (
                                                                        <blockquote
                                                                                key={excerpt}
                                                                                className="border-l-2 border-[var(--controversy)] pl-3 text-sm text-[var(--ink)] font-serif"
                                                                        >
                                                                                “{excerpt}”
                                                                        </blockquote>
                                                                ))}
                                                        </div>
                                                )}

                                                {(finding.replacement_suggestions || []).length > 0 && (
                                                        <div className="mt-4 border border-[var(--line)] bg-[var(--paper)] p-4">
                                                                <div className="overline text-[var(--truth)] mb-3">Frases substitutas</div>
                                                                <div className="space-y-3">
                                                                        {finding.replacement_suggestions.map((suggestion) => (
                                                                                <div
                                                                                        key={suggestion}
                                                                                        className="flex flex-col sm:flex-row sm:items-start gap-3 border-b border-[var(--line)] last:border-b-0 pb-3 last:pb-0"
                                                                                >
                                                                                        <p className="flex-1 text-sm text-[var(--ink)] font-serif leading-relaxed">
                                                                                                {suggestion}
                                                                                        </p>
                                                                                        <button
                                                                                                type="button"
                                                                                                onClick={() => copySuggestion(suggestion)}
                                                                                                className="overline px-3 py-2 border border-[var(--line-strong)] text-[var(--ink-soft)] hover:text-[var(--ink)] hover:bg-[var(--bg-deep)] inline-flex items-center justify-center gap-2 shrink-0"
                                                                                        >
                                                                                                <Copy size={13} strokeWidth={1.5} />
                                                                                                Copiar
                                                                                        </button>
                                                                                </div>
                                                                        ))}
                                                                </div>
                                                        </div>
                                                )}

                                                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                                        <div>
                                                                <div className="overline text-[var(--ink-faint)] mb-1">Motivo</div>
                                                                <p className="text-[var(--ink-soft)]">{finding.rationale}</p>
                                                        </div>
                                                        <div>
                                                                <div className="overline text-[var(--ink-faint)] mb-1">Ação sugerida</div>
                                                                <p className="text-[var(--ink-soft)]">{finding.suggested_action}</p>
                                                        </div>
                                                </div>
                                        </article>
                                ))}

                                {!scanning && findings.length === 0 && (
                                        <div className="border border-dashed border-[var(--line-strong)] p-8 text-center text-sm text-[var(--ink-faint)]">
                                                Nenhuma análise executada nesta sessão.
                                        </div>
                                )}
                        </div>
                </div>
        );
};
